import React, { useState, useEffect, useRef } from "react";
import LeadCard from "./LeadCard";
import { FiMoreVertical, FiTrash2, FiEdit2, FiPlus, FiMove } from "react-icons/fi";

const KanbanColumn = ({
  stage,
  leads = [],
  onDropLead,
  onDropColumnBefore,
  onDropColumnAfter,
  onOpenLeadDetails,
  onEditStage,
  onDeleteStage,
  onAddLead,
  onLeadDragStart,
  onLeadDragEnd,
  onColumnDragStart,
  onColumnDragEnd,
  draggingLeadId = null,
  draggingColumnId = null,
  isAdmin = true,
  selectMode = false,
  selectedLeadIds = new Set(),
  onToggleSelectLead,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [leadDragOver, setLeadDragOver] = useState(false);
  const [colDropSide, setColDropSide] = useState(null); // 'left' | 'right' | null
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const isLead = (e) => e.dataTransfer.types.includes("application/x-lead-id");
  const isColumn = (e) =>
    e.dataTransfer.types.includes("application/x-stage-id");

  // === LEAD drop zone ===
  const handleLeadDragOver = (e) => {
    if (!isLead(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setLeadDragOver(true);
  };

  const handleLeadDragLeave = (e) => {
    if (!isLead(e)) return;
    setLeadDragOver(false);
  };

  const handleLeadDrop = (e) => {
    if (!isLead(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setLeadDragOver(false);
    const leadId = e.dataTransfer.getData("application/x-lead-id");
    if (leadId) {
      onDropLead?.(leadId, stage._id, leads.length);
    }
  };

  // === COLUMN drop zone (admin only column reorder) ===
  const handleColumnDragOver = (e) => {
    if (!isColumn(e) || !isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    setColDropSide(e.clientX < midpoint ? "left" : "right");
  };

  const handleColumnDragLeave = (e) => {
    if (!isColumn(e)) return;
    setColDropSide(null);
  };

  const handleColumnDrop = (e) => {
    if (!isColumn(e) || !isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    const fromStageId = e.dataTransfer.getData("application/x-stage-id");
    const side = colDropSide;
    setColDropSide(null);
    if (!fromStageId || fromStageId === stage._id) return;
    if (side === "left") onDropColumnBefore?.(fromStageId, stage._id);
    else onDropColumnAfter?.(fromStageId, stage._id);
  };

  // === Header drag handle (initiates column drag) ===
  const handleHeaderDragStart = (e) => {
    if (!isAdmin) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-stage-id", stage._id);
    e.dataTransfer.setData("text/plain", stage._id);
    onColumnDragStart?.(stage._id);
  };

  const handleHeaderDragEnd = () => {
    onColumnDragEnd?.();
  };

  const isThisColumnDragging = draggingColumnId === stage._id;

  return (
    <div
      onDragOver={handleColumnDragOver}
      onDragLeave={handleColumnDragLeave}
      onDrop={handleColumnDrop}
      className={`
        relative flex-shrink-0 w-[min(88vw,20rem)] sm:w-80 max-w-[100%]
        bg-gray-50 rounded-lg border border-gray-200 flex flex-col transition-all
        max-h-[min(70vh,calc(100dvh-14rem))] sm:max-h-[calc(100vh-280px)]
        ${isThisColumnDragging ? "opacity-40" : ""}
      `}
    >
      {colDropSide === "left" && isAdmin && (
        <div className="absolute -left-2 top-0 bottom-0 w-1 bg-blue-500 rounded-full pointer-events-none" />
      )}
      {colDropSide === "right" && isAdmin && (
        <div className="absolute -right-2 top-0 bottom-0 w-1 bg-blue-500 rounded-full pointer-events-none" />
      )}

      {/* Column Header */}
      <div className="p-3 border-b border-gray-200 bg-white rounded-t-lg flex items-center justify-between">
        <div
          draggable={isAdmin}
          onDragStart={handleHeaderDragStart}
          onDragEnd={handleHeaderDragEnd}
          className={`flex items-center gap-2 flex-1 min-w-0 select-none ${
            isAdmin ? "cursor-grab active:cursor-grabbing" : ""
          }`}
          title={isAdmin ? "Drag to reorder column" : ""}
        >
          {isAdmin && (
            <FiMove size={14} className="text-gray-400 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 text-sm truncate">
              {stage.columnName}
            </h3>
            {stage.description && (
              <p className="text-[10px] text-gray-600 truncate italic mt-0.5">
                {stage.description}
              </p>
            )}
            <div className="space-y-0.5 mt-1">
              <p className="text-[11px] text-gray-500">
                {leads.length} {leads.length === 1 ? "lead" : "leads"}
              </p>
              {leads.some((l) => l.expectedDealRevenue) && (
                <p className="text-[11px] text-green-600 font-medium">
                  PKR {""}
                  {leads
                    .reduce(
                      (sum, l) => sum + (Number(l.expectedDealRevenue) || 0),
                      0,
                    )
                    .toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu((v) => !v);
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="More options"
            >
              <FiMoreVertical size={16} className="text-gray-600" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 z-50 min-w-[180px]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditStage?.(stage);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
                >
                  <FiEdit2 size={14} />
                  Edit Stage
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteStage?.(stage);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-red-50 text-sm text-red-600 flex items-center gap-2 border-t border-gray-100"
                >
                  <FiTrash2 size={14} />
                  Delete Stage
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Column Content - Scrollable Area */}
      <div
        onDragOver={handleLeadDragOver}
        onDragLeave={handleLeadDragLeave}
        onDrop={handleLeadDrop}
        className={`
          flex-1 p-2 space-y-2 overflow-y-auto min-h-[120px]
          transition-colors
          ${leadDragOver ? "bg-blue-50 ring-2 ring-blue-300 ring-inset" : ""}
        `}
      >
        {leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-gray-400 text-xs">No leads in this stage</p>
            <p className="text-gray-300 text-[11px] mt-1">
              Drop leads here or click "Add Lead"
            </p>
          </div>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead._id}
              lead={lead}
              onOpenDetails={onOpenLeadDetails}
              onDragStart={onLeadDragStart}
              onDragEnd={onLeadDragEnd}
              isDragging={draggingLeadId === lead._id}
              draggable
              selectMode={selectMode}
              isSelected={selectedLeadIds.has(lead._id)}
              onToggleSelect={onToggleSelectLead}
            />
          ))
        )}
      </div>

      {/* Column Footer - Add Lead Button */}
      <div className="p-2 border-t border-gray-200 bg-white rounded-b-lg">
        <button
          type="button"
          onClick={() => onAddLead?.(stage)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded transition-colors border border-dashed border-gray-300 hover:border-blue-300"
        >
          <FiPlus size={14} />
          Add Lead
        </button>
      </div>
    </div>
  );
};

export default KanbanColumn;
